import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Sucursal } from '../types/stores';
import { environment } from '../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { EmailValidationService } from './email-validation.service';
import {
  LOCAL_STORAGE_KEY_AUTH_TOKEN,
  LOCAL_STORAGE_KEY_SELECTED_STORE,
  LOCAL_STORAGE_KEY_USER_DATA,
} from '../config/variables';
import { preloadedStores } from '../data-preloaded/stores-data';
import { NotificationService } from './notification.service';
import { AuthStateService } from './auth-state.service';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  private selectedStoreSubject = new BehaviorSubject<Sucursal | null>(null);
  public selectedStore$ = this.selectedStoreSubject.asObservable();
  readonly emailValidationService = inject(EmailValidationService);
  readonly notificationService = inject(NotificationService);
  readonly authStateService = inject(AuthStateService);

  constructor() {
    this.loadSelectedStore();
    this.setupAuthStateListener();
  }

  getSelectedStore(): Sucursal | null {
    return this.selectedStoreSubject.value;
  }

  updateStore(store: Sucursal): void {
    const userData = localStorage.getItem(LOCAL_STORAGE_KEY_USER_DATA);
    if (!userData) this.saveSelectedStoreToStorage(store);
    this.updatePreferredStoreInBackend(store);
  }

  setSelectedStore(store: Sucursal | null) {
    this.selectedStoreSubject.next(store);
    this.saveSelectedStoreToStorage(store);
  }

  private saveSelectedStoreToStorage(store: Sucursal | null): void {
    if (typeof window === 'undefined') return;

    const storeJson = JSON.stringify(store);
    localStorage.setItem(LOCAL_STORAGE_KEY_SELECTED_STORE, storeJson);
  }

  private async loadSelectedStore() {
    const selectedStoreFromAuth = await this.tryToGetSelectedStoreFromAuth();
    const selectedStoreFromStorage = this.loadSelectedStoreFromStorage();
    const selectedStore =
      selectedStoreFromAuth ?? selectedStoreFromStorage ?? null;

    this.setSelectedStore(selectedStore);
  }

  private async tryToGetSelectedStoreFromAuth() {
    try {
      const { data } = await firstValueFrom(
        this.emailValidationService.getMe()
      );
      if (!data) return null;

      const store = (preloadedStores as Sucursal[]).find(
        (store) =>
          store.id === (data.sucursalPreferida as Sucursal)?.id ||
          store.id === (data.sucursalPreferida as string)
      );

      return store || null;
    } catch (error) {
      return null;
    }
  }

  private loadSelectedStoreFromStorage() {
    if (typeof window === 'undefined') return null;

    const stored = localStorage.getItem(LOCAL_STORAGE_KEY_SELECTED_STORE);

    if (stored) return JSON.parse(stored) as Sucursal;

    return null;
  }

  /**
   * Actualiza la sucursal preferida en el backend
   */
  private updatePreferredStoreInBackend(store: Sucursal): void {
    // Solo ejecutar en el navegador
    if (!isPlatformBrowser(PLATFORM_ID)) return;

    this.emailValidationService
      .updateSucursalPreferida({
        sucursalPreferida: store.id,
      })
      .subscribe({
        next: () => {
          this.setSelectedStore(store);
          this.notificationService.showSuccess(
            'Sucursal preferida actualizada correctamente'
          );
        },
        error: (error) => {
          this.notificationService.showError(
            'Error actualizando sucursal preferida',
            error.message
          );
        },
      });
  }

  /**
   * Configura el listener para eventos de autenticación
   */
  private setupAuthStateListener(): void {
    this.authStateService.authState$.subscribe((authChange) => {
      if (authChange.type === 'logout') {
        this.setSelectedStore(null);
      } else {
        this.loadSelectedStore();
      }
    });
  }
}
